import { protocolVisitIndex } from '@/components/admin/sessions/walk/walk-labels';
import { determinationBlockDrawn } from '@/lib/session-noyau';
import { rangementBlockDrawn } from '@/lib/session-rangement';
import type { NoyauDetermination, SessionDetail } from '@matter_extractor/shared';

/**
 * The SOMMAIRE of the parcours (refonte-modulaire.md §12.2 « Depuis #221 ») - pure, the régime of
 * `session-head.ts`: the component only draws, and the entries are computed from the DATA of the
 * detail, never read back from the document.
 *
 * ## The order is the order of the screen
 *
 * The list reads top to bottom as the Parcours tab does: the pages in the order traversed, the
 * protocol block at the visit that holds the turns - followed by the rangement and the
 * determination it launched, as `SessionWalk` hangs them (« Rangement », « Noyau · PSG 56 », the
 * maquette's sub-entries) - and then the sections of the blank trials (« Lecture à blanc »,
 * « Rangement à blanc »), which the tab puts AFTER the fil of the pages (#219) and, since #224,
 * only for a session that lacks the block the trial would stand in the foot of. The two
 * conditions of the blocks are the walk's own (`rangementBlockDrawn`, `determinationBlockDrawn`).
 *
 * ## The anchors are ids, stable and minimal
 *
 * Each target carries an `id` built here (`pageAnchor`, `TOC_ANCHORS`) and written on its element by
 * the component that draws it, so the entry and its target cannot drift apart on a string typed
 * twice. A page card is `parcours-page-<rank>` (the rank both screens print, 1-based): the protocol
 * block, which IS the card of its visit, takes the same id; only a protocol that no visit holds
 * (a session that stopped before it) has an id of its own, `parcours-protocole`.
 */

/** The ids of the blocks that are not a page card. */
export const TOC_ANCHORS = {
  /** The protocol block of a session whose pages never held a turn (no visit to hang it on). */
  protocol: 'parcours-protocole',
  /** « Rangement de la matière » (§7.1 of scenario-succes.md), under the protocol's. */
  rangement: 'parcours-rangement',
  /** « Détermination du noyau » (noyau.md §9.1), under the rangement. */
  determination: 'parcours-noyau',
  /** The section « Lecture à blanc » (noyau.md §9.4). */
  noyauTrial: 'parcours-lecture-a-blanc',
  /** The section « Rangement à blanc » (scenario-succes.md §7.3). */
  rangementTrial: 'parcours-rangement-a-blanc',
} as const;

/** The id of the card of a visit, by the rank the card prints; the protocol's own when it has none. */
export function pageAnchor(rank: number | null): string {
  return rank === null ? TOC_ANCHORS.protocol : `parcours-page-${rank}`;
}

/**
 * What an entry is, which is what the column styles it by:
 * - `page` - a card of the fil, with its rank;
 * - `protocol` - the protocol block, in bleu nuit weight like its own border;
 * - `block` - a block hung under the protocol (rangement, determination), indented;
 * - `section` - a section of the blank trials, after the fil.
 */
export type TocKind = 'page' | 'protocol' | 'block' | 'section';

export type TocEntry = {
  /** The id of the element it leads to. */
  readonly anchor: string;
  readonly kind: TocKind;
  /** The 1-based rank a page card prints; null on everything else. */
  readonly rank: number | null;
  readonly label: string;
};

export const TOC_TITLE = 'Sommaire';

export const TOC_LABELS = {
  protocol: 'Protocole',
  protocolSuffix: ' · protocole',
  /** The maquette's sub-entries: « Rangement », and « Noyau · PSG 56 » once the principal is known. */
  rangement: 'Rangement',
  determination: 'Noyau',
  noyauTrial: 'Lecture à blanc',
  rangementTrial: 'Rangement à blanc',
} as const;

/**
 * Which sections of the blank trials the route mounts under the fil (#224): only the ones a block
 * does not carry. A session with a block of determination has its analyst's trial in the foot of
 * that block, and the same for the ranger; the section « Essais à blanc » is kept only for the
 * trial whose block the session does not have, and the vitrine - which has a note in place of the
 * trials - lists none.
 */
export type SessionTocTrials = {
  readonly noyau: boolean;
  readonly rangement: boolean;
};

export type SessionTocOptions = {
  readonly trials: SessionTocTrials;
};

/** « Protocole » when the page is already called so, « Le trajet · protocole » otherwise. */
export function protocolTocLabel(pageName: string): string {
  return pageName.trim().toLowerCase() === TOC_LABELS.protocol.toLowerCase()
    ? TOC_LABELS.protocol
    : `${pageName}${TOC_LABELS.protocolSuffix}`;
}

/**
 * « Noyau · PSG 56 » (maquette `Vue de session`): the principal of a determination that rendered
 * and its note, as the six notes carry it; plain « Noyau » for every other state.
 */
export function determinationTocLabel(determination: NoyauDetermination | null): string {
  if (determination === null || determination.state !== 'rendered') return TOC_LABELS.determination;
  const { principal } = determination;
  if (principal === null) return TOC_LABELS.determination;
  const note = determination.scores.find((score) => score.noyau === principal)?.score;
  return note === undefined
    ? `${TOC_LABELS.determination} · ${principal}`
    : `${TOC_LABELS.determination} · ${principal} ${note}`;
}

export function sessionTocEntries(
  detail: SessionDetail,
  { trials }: SessionTocOptions,
): readonly TocEntry[] {
  const protocolAt = protocolVisitIndex(detail);
  const entries: TocEntry[] = [];

  /** The blocks the protocol launched, in the order the walk hangs them. */
  const pushHung = (): void => {
    if (rangementBlockDrawn(detail)) {
      entries.push({
        anchor: TOC_ANCHORS.rangement,
        kind: 'block',
        rank: null,
        label: TOC_LABELS.rangement,
      });
    }
    if (determinationBlockDrawn(detail)) {
      entries.push({
        anchor: TOC_ANCHORS.determination,
        kind: 'block',
        rank: null,
        label: determinationTocLabel(detail.noyau?.determination ?? null),
      });
    }
  };

  detail.pages.forEach((page, position) => {
    const rank = position + 1;
    if (position === protocolAt) {
      entries.push({
        anchor: pageAnchor(rank),
        kind: 'protocol',
        rank,
        label: protocolTocLabel(page.name),
      });
      pushHung();
    } else {
      entries.push({ anchor: pageAnchor(rank), kind: 'page', rank, label: page.name });
    }
  });

  if (protocolAt === null) {
    // The block that no visit holds is appended after the pages, with what it launched.
    entries.push({
      anchor: TOC_ANCHORS.protocol,
      kind: 'protocol',
      rank: null,
      label: TOC_LABELS.protocol,
    });
    pushHung();
  }

  if (trials.noyau) {
    entries.push({
      anchor: TOC_ANCHORS.noyauTrial,
      kind: 'section',
      rank: null,
      label: TOC_LABELS.noyauTrial,
    });
  }
  if (trials.rangement) {
    entries.push({
      anchor: TOC_ANCHORS.rangementTrial,
      kind: 'section',
      rank: null,
      label: TOC_LABELS.rangementTrial,
    });
  }

  return entries;
}
